/**
 * Client-side validation for the auth forms.
 *
 * **These rules must mirror `auth.validator.js` in the backend exactly.** They
 * exist to give immediate feedback, not to enforce anything — the backend is
 * the authority and re-checks every one of them. If the two ever disagree, the
 * user gets a field error from the server that the form could not predict,
 * which reads as a bug even though nothing is actually broken.
 *
 * Backend rule, kept in sync by hand: at least 12 characters, at most 128, and
 * at least one lower-case letter, one upper-case letter and one number.
 */

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PASSWORD_RULES = [
  { key: 'length', label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { key: 'lower', label: 'A lower-case letter', test: (v) => /[a-z]/.test(v) },
  { key: 'upper', label: 'An upper-case letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'number', label: 'A number', test: (v) => /[0-9]/.test(v) },
];

/** Which rules a candidate password currently satisfies. */
export function checkPassword(value) {
  return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(value ?? '') }));
}

/** One message for a password field, or undefined when it is acceptable. */
export function validatePassword(value) {
  if (!value) return 'Choose a password.';
  if (value.length > 128) return 'Use 128 characters or fewer.';

  const unmet = checkPassword(value).filter((rule) => !rule.met);
  if (unmet.length === 0) return undefined;

  return `Password still needs: ${unmet.map((r) => r.label.toLowerCase()).join(', ')}.`;
}

/** One message for an email field, or undefined when it is acceptable. */
export function validateEmail(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'Enter your email address.';
  if (!EMAIL_PATTERN.test(trimmed)) return 'Enter a valid email address.';
  return undefined;
}
